import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../lib/api';
import { rememberReturnTo } from '../lib/returnTo';
import { supabase } from '../lib/supabase';
import { usePricing } from '../hooks/usePricing';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  MapPin, Clock, Star, ShieldCheck, Phone, Copy, Check, MessageCircle,
  AlertCircle, UserCheck, Send, X, Loader2, Plus, FileCheck,
} from 'lucide-react';

const REQUEST_STATUS_BADGE = {
  open: { labelKey: 'services.reqStatusOpen', className: 'bg-green-50 text-green-700 border-green-200' },
  picked: { labelKey: 'services.reqStatusPicked', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  closed: { labelKey: 'services.reqStatusClosed', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  expired: { labelKey: 'services.reqStatusExpired', className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const RESPONSE_STATUS_BADGE = {
  submitted: { labelKey: 'services.reqResponseStatus_submitted', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  picked: { labelKey: 'services.reqResponseStatus_picked', className: 'bg-green-50 text-green-700 border-green-200' },
  withdrawn: { labelKey: 'services.reqResponseStatus_withdrawn', className: 'bg-red-50 text-red-700 border-red-200' },
};

/** Renders the 1–5 star row for a rating (mirrors ServiceListingDetail.StarRow). */
const StarRow = ({ rating, t }) => (
  <div
    className="flex items-center gap-0.5"
    role="img"
    aria-label={t('services.ratingOutOf', { rating })}
  >
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} size={13} className={s <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} aria-hidden="true" />
    ))}
  </div>
);

/** Budget label from a request's optional min/max. */
const budgetLabel = (request, t) => {
  const { budget_min: min, budget_max: max } = request;
  if (min != null && max != null) {
    return t('services.reqBudgetRange', { min: Number(min).toLocaleString(), max: Number(max).toLocaleString() });
  }
  if (min != null) return t('services.reqBudgetFrom', { amount: Number(min).toLocaleString() });
  if (max != null) return t('services.reqBudgetUpTo', { amount: Number(max).toLocaleString() });
  return t('services.reqBudgetOpen');
};

/** Human "expires in N days" from an ISO timestamp. */
const expiresLabel = (expiresAt, t) => {
  if (!expiresAt) return '';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return '';
  if (ms <= 0) return t('services.reqExpired');
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  if (days <= 0) return t('services.reqExpiresToday');
  return t('services.reqExpiresIn', { count: days });
};

/** Revealed-contact block after picking a Pro (reuses ShowContactButton's reveal-display pattern). */
const RevealedContact = ({ contact, t }) => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const copyPhone = async () => {
    if (!contact?.phone) return;
    try {
      await navigator.clipboard.writeText(contact.phone);
      setCopied(true);
      toast.success(t('services.phoneCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('services.copyFailed'));
    }
  };

  return (
    <Card className="p-4 space-y-3 border-green-200 bg-green-50/40">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2 text-green-800">
        <UserCheck size={18} aria-hidden="true" />
        {t('services.reqRevealedTitle')}
      </h2>
      {contact?.phone ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background p-3">
          <a href={`tel:${contact.phone}`} className="flex items-center gap-2 font-semibold text-primary">
            <Phone size={16} aria-hidden="true" />
            {contact.phone}
          </a>
          <Button size="sm" variant="outline" className="min-h-[40px] px-4" onClick={copyPhone}>
            {copied ? <Check size={14} className="mr-1" aria-hidden="true" /> : <Copy size={14} className="mr-1" aria-hidden="true" />}
            {copied ? t('services.copied') : t('services.copy')}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('services.noPhoneOnFile')}</p>
      )}
      {contact?.message_deeplink && (
        <Button className="w-full min-h-[44px]" onClick={() => navigate(contact.message_deeplink)}>
          <MessageCircle size={16} className="mr-2" aria-hidden="true" />
          {t('services.messagePro')}
        </Button>
      )}
      <p className="text-xs text-muted-foreground">{t('services.revealFollowupHint')}</p>
    </Card>
  );
};

/**
 * Role-adaptive service-request detail page (route /services/requests/:id).
 * GET /api/service-requests/{id} returns different shapes per caller:
 *   - OWNER: { request, responses:[...] } → summary + responses list + pick/close actions.
 *   - PRO (matching/responded): { request (no client identity), my_response? } → info + respond/withdraw.
 *   - OTHERS: 404 → friendly not-found.
 * Flag-gated shell when services_enabled is false.
 */
const ServiceRequestDetail = ({ profile, setProfile }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { pricing, loading: pricingLoading } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);

  const [request, setRequest] = useState(null);
  const [responses, setResponses] = useState(null); // array → owner view; null → pro/other
  const [myResponse, setMyResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Owner: pick flow
  const [pickTarget, setPickTarget] = useState(null); // response object pending confirm
  const [revealedContact, setRevealedContact] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [expandedResponses, setExpandedResponses] = useState({}); // { [responseId]: true }

  // Pro: respond flow
  const [myListings, setMyListings] = useState([]);
  const [listingId, setListingId] = useState('');
  const [respMessage, setRespMessage] = useState('');
  const [respRate, setRespRate] = useState('');
  const [respTimeline, setRespTimeline] = useState('');
  const [respError, setRespError] = useState('');
  const [respSubmitting, setRespSubmitting] = useState(false);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);

  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;
    return { Authorization: `Bearer ${session.access_token}` };
  };

  // Monotonic counter so only the latest in-flight fetchDetail commits its result. Guards
  // against out-of-order responses (rapid action→refetch overlap, or id changing mid-flight).
  const fetchSeqRef = useRef(0);

  const fetchDetail = useCallback(async () => {
    // Keep the spinner up while pricing is unresolved (do NOT flip loading off) so a cold
    // visit to a shared/notification link never flashes the not-found screen.
    if (!servicesEnabled) return;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setNotFound(false);
    try {
      const headers = await authHeaders();
      if (seq !== fetchSeqRef.current) return;
      // No session → send to auth with a returnTo back here (matches ShowContactButton),
      // instead of stranding the user on a permanent not-found screen.
      if (!headers) {
        // replace (not push): this redirect fires automatically on mount, so Back from the
        // login screen must NOT return here and re-trigger the redirect (back-button trap).
        // The destination goes through rememberReturnTo — router state does not survive /auth's
        // redirect to /sign-up or the full reload each auth transition performs.
        rememberReturnTo(`/services/requests/${id}`);
        navigate('/auth', { replace: true });
        return;
      }
      const res = await api.get(`/api/service-requests/${id}`, { headers });
      if (seq !== fetchSeqRef.current) return;
      const data = res.data || {};
      const reqData = data.request || null;
      setRequest(reqData);
      // Owner/Pro payloads nest responses / my_response INSIDE data.request (not at the top
      // level) — reading them off `data` made responses always undefined, so the request OWNER
      // fell into the Pro view and could never pick a Pro (live-caught walkthrough bug).
      setResponses(Array.isArray(reqData?.responses) ? reqData.responses : null);
      setMyResponse(reqData?.my_response || null);
    } catch (err) {
      if (seq === fetchSeqRef.current) setNotFound(true);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [id, servicesEnabled, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // A different request id → clear state carried over from the previous request so a revealed phone
  // number, a pending pick, a draft response, or an expanded card can never bleed across requests if
  // the page is reused on a client-side :id change (fetchDetail only resets the fetched payload, and
  // it also runs on action-refetch where wiping drafts/expansion would be wrong).
  useEffect(() => {
    setRevealedContact(null);
    setPickTarget(null);
    setExpandedResponses({});
    setRespMessage('');
    setRespRate('');
    setRespTimeline('');
    setRespError('');
    setCloseConfirmOpen(false);
    setWithdrawConfirmOpen(false);
  }, [id]);

  // Is this the owner view? Owner payload includes a responses array (possibly empty).
  const isOwnerView = Array.isArray(responses);
  const isProView = !isOwnerView;

  // Pro respond form only needs listings when the Pro has not yet responded and the request is open.
  const canRespond = isProView && !myResponse && request?.status === 'open';

  // Fetch the Pro's own listings when a respond form is possible. Keyed on `id` so listing
  // selection state never carries across a client-side route change to a different request.
  useEffect(() => {
    if (!canRespond) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        if (!headers) return;
        const res = await api.get('/api/pro-listings/mine', { headers });
        if (cancelled) return;
        const active = (res.data?.listings || []).filter((l) => l.status === 'active');
        setMyListings(active);
        // Preselect only when exactly one; otherwise drop any stale selection not in the set.
        setListingId((prev) => {
          if (active.length === 1) return active[0].id;
          return active.some((l) => l.id === prev) ? prev : '';
        });
      } catch (err) {
        if (!cancelled) { setMyListings([]); setListingId(''); }
      }
    })();
    return () => { cancelled = true; };
  }, [canRespond, id]);

  const runPick = async (response) => {
    if (actionBusy || !response?.id) return;
    setActionBusy(true);
    try {
      const headers = await authHeaders();
      if (!headers) { toast.error(t('services.loginRequired')); return; }
      const res = await api.post(
        `/api/service-requests/${id}/pick/${response.id}`,
        {},
        { headers },
      );
      setRevealedContact(res.data || {});
      toast.success(t('services.reqPickSuccess'));
      setPickTarget(null);
      await fetchDetail();
    } catch (err) {
      const status = err?.response?.status;
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      if (status === 409) {
        toast.error(detail || t('services.reqPickConflict'));
        await fetchDetail();
      } else {
        toast.error(detail || t('services.reqErrGeneric'));
      }
      setPickTarget(null);
    } finally {
      setActionBusy(false);
    }
  };

  const runClose = async () => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const headers = await authHeaders();
      if (!headers) { toast.error(t('services.loginRequired')); return; }
      await api.delete(`/api/service-requests/${id}`, { headers });
      toast.success(t('services.reqCloseSuccess'));
      setCloseConfirmOpen(false);
      await fetchDetail();
    } catch (err) {
      const status = err?.response?.status;
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      if (status === 409) {
        toast.error(detail || t('services.reqClosePicked'));
      } else {
        toast.error(detail || t('services.reqCloseError'));
      }
      setCloseConfirmOpen(false);
    } finally {
      setActionBusy(false);
    }
  };

  const mapRespondError = (err) => {
    const status = err?.response?.status;
    const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
    if (status === 400) return detail || t('services.reqRespondErrMismatch');
    if (status === 409) return detail || t('services.reqRespondErrAlready');
    if (status === 410) return detail || t('services.reqRespondErrExpired');
    return detail || t('services.reqErrGeneric');
  };

  const submitResponse = async () => {
    if (respSubmitting) return;
    setRespError('');
    if (!listingId) { setRespError(t('services.reqRespondErrListingRequired')); return; }
    const trimmed = respMessage.trim();
    if (!trimmed) { setRespError(t('services.reqRespondErrMessageRequired')); return; }
    if (trimmed.length < 5 || trimmed.length > 1000) {
      setRespError(t('services.reqRespondErrMessageLength'));
      return;
    }

    setRespSubmitting(true);
    try {
      const headers = await authHeaders();
      if (!headers) { toast.error(t('services.loginRequired')); return; }
      const payload = { listing_id: listingId, message: trimmed };
      if (respRate !== '') payload.quoted_rate = Number(respRate);
      if (respTimeline.trim() !== '') payload.quoted_timeline = respTimeline.trim();
      await api.post(`/api/service-requests/${id}/respond`, payload, { headers });
      toast.success(t('services.reqRespondSuccess'));
      await fetchDetail();
    } catch (err) {
      const message = mapRespondError(err);
      setRespError(message);
      toast.error(message);
    } finally {
      setRespSubmitting(false);
    }
  };

  const runWithdraw = async () => {
    if (actionBusy || !myResponse?.id) return;
    setActionBusy(true);
    try {
      const headers = await authHeaders();
      if (!headers) { toast.error(t('services.loginRequired')); return; }
      await api.post(
        `/api/service-requests/${id}/responses/${myResponse.id}/withdraw`,
        {},
        { headers },
      );
      toast.success(t('services.reqWithdrawSuccess'));
      setWithdrawConfirmOpen(false);
      await fetchDetail();
    } catch (err) {
      const detail = typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : '';
      toast.error(detail || t('services.reqWithdrawError'));
      setWithdrawConfirmOpen(false);
    } finally {
      setActionBusy(false);
    }
  };

  // --- Render guards ---------------------------------------------------------

  if (!pricingLoading && !servicesEnabled) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-3">{t('services.comingSoonTitle')}</h1>
          <p className="text-muted-foreground">{t('services.comingSoonBody')}</p>
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  if (pricingLoading || loading) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="flex justify-center py-24">
          <div className="spinner" role="status" aria-label={t('common.loading', 'Loading...')} />
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  if (notFound || !request) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="text-xl font-semibold mb-3">{t('services.reqDetailNotFoundTitle')}</h1>
          <p className="text-muted-foreground mb-6">{t('services.reqDetailNotFoundBody')}</p>
          <Button onClick={() => navigate('/services')}>{t('services.reqBackToServices')}</Button>
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  const statusBadge = REQUEST_STATUS_BADGE[request.status] || REQUEST_STATUS_BADGE.open;
  const canPick = request.status === 'open';

  // --- Shared request summary -----------------------------------------------

  const requestSummary = (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <h1 className="font-heading text-xl sm:text-2xl font-bold break-words">{request.title}</h1>
        <Badge variant="outline" className={`shrink-0 ${statusBadge.className}`}>
          {t(statusBadge.labelKey)}
        </Badge>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {request.municipality && (
          <span className="flex items-center gap-1">
            <MapPin size={14} aria-hidden="true" />{request.municipality}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Clock size={14} aria-hidden="true" />{t(`services.reqUrgency_${request.urgency}`)}
        </span>
        {request.expires_at && (
          <span>{expiresLabel(request.expires_at, t)}</span>
        )}
      </div>
      <p className="text-sm font-semibold text-primary">{budgetLabel(request, t)}</p>
      {request.description && (
        <p className="text-sm text-muted-foreground whitespace-pre-line break-words">{request.description}</p>
      )}
    </Card>
  );

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {requestSummary}

        {/* Reveal block appears immediately after a successful pick. The aria-live wrapper is
            PERSISTENT (rendered before content arrives) so the reveal is reliably announced. */}
        <div aria-live="polite">
          {revealedContact && <RevealedContact contact={revealedContact} t={t} />}
        </div>

        {/* ---------------- OWNER VIEW ---------------- */}
        {isOwnerView && (
          <>
            <div>
              <h2 className="font-heading text-xl font-semibold mb-3">
                {t('services.reqResponsesHeading')}
                {responses.length > 0 ? ` (${responses.length})` : ''}
              </h2>
              {responses.length === 0 ? (
                <Card className="border-dashed p-6 text-center text-sm text-muted-foreground">
                  {t('services.reqNoResponsesYet')}
                </Card>
              ) : (
                <div className="space-y-3">
                  {responses.map((r) => {
                    const rBadge = RESPONSE_STATUS_BADGE[r.status] || RESPONSE_STATUS_BADGE.submitted;
                    const expanded = Boolean(expandedResponses[r.id]);
                    const message = r.message || '';
                    // Expander gate must match the LINE-based clamp: short multi-line messages
                    // (under 180 chars but >3 lines) also need the Read-more button.
                    const isLong = message.length > 180 || message.split('\n').length > 3;
                    // Persistent picked-contact: if the payload carries contact/deeplink for the
                    // picked response, keep it visible after reload (backend may add these fields).
                    const showPickedContact = r.status === 'picked' && (r.pro_phone || r.message_deeplink);
                    return (
                      <Card key={r.id} className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-2 min-w-0 flex-wrap">
                              <span className="font-medium truncate">
                                {r.pro_first_name || t('services.thisPro')}
                              </span>
                              {r.id_verified && (
                                <Badge variant="verified" className="px-2 py-0.5 text-[10px]">
                                  <ShieldCheck size={10} className="mr-0.5" aria-hidden="true" />
                                  {t('services.verified')}
                                </Badge>
                              )}
                              {r.rating != null && <StarRow rating={Math.round(Number(r.rating))} t={t} />}
                            </div>
                            {r.quoted_timeline && (
                              <p className="text-xs text-muted-foreground">
                                <span>{t('services.reqQuotedTimeline')}: </span>
                                <span className="font-medium text-foreground">{r.quoted_timeline}</span>
                              </p>
                            )}
                            {r.listing_title && (
                              <p className="text-xs text-muted-foreground truncate" title={r.listing_title}>
                                {r.listing_title}
                              </p>
                            )}
                            {Array.isArray(r.credential_types) && r.credential_types.length > 0 && (
                              <p className="flex items-center gap-1 text-xs text-muted-foreground min-w-0">
                                <FileCheck size={12} className="shrink-0 text-emerald-600" aria-hidden="true" />
                                <span className="truncate">
                                  {r.credential_types.map((c) => t(`services.verifDocType_${c}`, c)).join(', ')}
                                </span>
                              </p>
                            )}
                          </div>
                          {/* Fixed comparison slot: quoted rate anchored top-right */}
                          <div className="shrink-0 text-right">
                            <p className="text-base font-bold text-primary">
                              {r.quoted_rate != null
                                ? `₱${Number(r.quoted_rate).toLocaleString()}`
                                : <span className="text-muted-foreground font-medium">{t('services.negotiable')}</span>}
                            </p>
                            <Badge variant="outline" className={`mt-1 ${rBadge.className}`}>{t(rBadge.labelKey)}</Badge>
                          </div>
                        </div>

                        {message && (
                          <div>
                            <p className={`text-sm whitespace-pre-line break-words ${expanded ? '' : 'line-clamp-3'}`}>
                              {message}
                            </p>
                            {isLong && (
                              <button
                                type="button"
                                onClick={() => setExpandedResponses((prev) => ({ ...prev, [r.id]: !expanded }))}
                                className="text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                              >
                                {expanded ? t('services.readLess') : t('services.readMore')}
                              </button>
                            )}
                          </div>
                        )}

                        {showPickedContact && (
                          <div className="rounded-lg border border-green-200 bg-green-50/40 p-3 space-y-2">
                            {r.pro_phone && (
                              <a href={`tel:${r.pro_phone}`} className="flex items-center gap-2 font-semibold text-primary">
                                <Phone size={16} aria-hidden="true" />
                                {r.pro_phone}
                              </a>
                            )}
                            {r.message_deeplink && (
                              <Button className="w-full min-h-[44px]" onClick={() => navigate(r.message_deeplink)}>
                                <MessageCircle size={16} className="mr-2" aria-hidden="true" />
                                {t('services.messagePro')}
                              </Button>
                            )}
                          </div>
                        )}

                        {canPick && r.status !== 'withdrawn' && (
                          <div className="pt-1">
                            <Button
                              className="w-full sm:w-auto min-h-[44px]"
                              disabled={actionBusy}
                              onClick={() => setPickTarget(r)}
                            >
                              <UserCheck size={16} className="mr-1" aria-hidden="true" />
                              {t('services.reqPickPro')}
                            </Button>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {request.status === 'open' && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  disabled={actionBusy}
                  onClick={() => setCloseConfirmOpen(true)}
                >
                  <X size={16} className="mr-1" aria-hidden="true" />
                  {t('services.reqCloseRequest')}
                </Button>
              </div>
            )}
          </>
        )}

        {/* ---------------- PRO VIEW ---------------- */}
        {isProView && myResponse && (
          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="font-heading text-lg font-semibold">{t('services.reqYourResponse')}</h2>
              <Badge
                variant="outline"
                className={(RESPONSE_STATUS_BADGE[myResponse.status] || RESPONSE_STATUS_BADGE.submitted).className}
              >
                {t((RESPONSE_STATUS_BADGE[myResponse.status] || RESPONSE_STATUS_BADGE.submitted).labelKey)}
              </Badge>
            </div>
            {myResponse.message && (
              <p className="text-sm whitespace-pre-line break-words">{myResponse.message}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {myResponse.quoted_rate != null && (
                <span>
                  <span className="text-muted-foreground">{t('services.reqQuotedRate')}: </span>
                  <span className="font-semibold">₱{Number(myResponse.quoted_rate).toLocaleString()}</span>
                </span>
              )}
              {myResponse.quoted_timeline && (
                <span>
                  <span className="text-muted-foreground">{t('services.reqQuotedTimeline')}: </span>
                  <span className="font-medium">{myResponse.quoted_timeline}</span>
                </span>
              )}
            </div>
            {myResponse.status === 'submitted' && (
              <div className="pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  disabled={actionBusy}
                  onClick={() => setWithdrawConfirmOpen(true)}
                >
                  <X size={14} className="mr-1" aria-hidden="true" />
                  {t('services.reqWithdraw')}
                </Button>
              </div>
            )}
          </Card>
        )}

        {canRespond && (
          <Card className="p-4 space-y-4">
            <h2 className="font-heading text-lg font-semibold">{t('services.reqRespondHeading')}</h2>

            {myListings.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{t('services.reqRespondNoListings')}</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate('/services/listings/create')}
                >
                  <Plus size={14} className="mr-1" aria-hidden="true" />
                  {t('services.listAService')}
                </Button>
              </div>
            ) : (
              <form
                className="space-y-4"
                onSubmit={(e) => { e.preventDefault(); submitResponse(); }}
              >
                <div className="space-y-2">
                  <Label htmlFor="resp-listing">{t('services.reqRespondListing')}</Label>
                  <Select value={listingId || undefined} onValueChange={setListingId}>
                    <SelectTrigger id="resp-listing" aria-label={t('services.reqRespondListing')}>
                      <SelectValue placeholder={t('services.reqRespondListingPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {myListings.map((l) => (
                        <SelectItem key={l.id} value={String(l.id)}>{l.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resp-message">{t('services.reqRespondMessage')}</Label>
                  <Textarea
                    id="resp-message"
                    value={respMessage}
                    maxLength={1000}
                    rows={4}
                    onChange={(e) => setRespMessage(e.target.value)}
                    placeholder={t('services.reqRespondMessagePlaceholder')}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="resp-rate">
                      {t('services.reqRespondRate')}{' '}
                      <span className="text-muted-foreground font-normal">({t('common.optional')})</span>
                    </Label>
                    <Input
                      id="resp-rate"
                      type="number"
                      min="0"
                      value={respRate}
                      onChange={(e) => setRespRate(e.target.value)}
                      placeholder="₱"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="resp-timeline">
                      {t('services.reqRespondTimeline')}{' '}
                      <span className="text-muted-foreground font-normal">({t('common.optional')})</span>
                    </Label>
                    <Input
                      id="resp-timeline"
                      value={respTimeline}
                      maxLength={100}
                      onChange={(e) => setRespTimeline(e.target.value)}
                      placeholder={t('services.reqRespondTimelinePlaceholder')}
                    />
                  </div>
                </div>

                {respError && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
                  >
                    <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{respError}</span>
                  </div>
                )}

                <Button type="submit" disabled={respSubmitting} className="w-full min-h-[44px]">
                  <Send size={16} className="mr-2" aria-hidden="true" />
                  {respSubmitting ? t('services.reqRespondSubmitting') : t('services.reqRespondSubmit')}
                </Button>
              </form>
            )}
          </Card>
        )}
      </div>

      {/* Pick confirm dialog */}
      <Dialog open={!!pickTarget} onOpenChange={(open) => { if (!open && !actionBusy) setPickTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck size={20} className="text-primary" aria-hidden="true" />
              {t('services.reqPickConfirmTitle', { name: pickTarget?.pro_first_name || t('services.thisPro') })}
            </DialogTitle>
            <DialogDescription>{t('services.reqPickConfirmBody')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={actionBusy} onClick={() => setPickTarget(null)}>
              {t('services.reqPickCancel')}
            </Button>
            <Button disabled={actionBusy} onClick={() => runPick(pickTarget)}>
              {actionBusy && <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />}
              {t('services.reqPickConfirmAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close confirm dialog */}
      <Dialog open={closeConfirmOpen} onOpenChange={(open) => { if (!open && !actionBusy) setCloseConfirmOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <X size={20} aria-hidden="true" />
              {t('services.reqCloseConfirmTitle')}
            </DialogTitle>
            <DialogDescription>{t('services.reqCloseConfirmBody')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={actionBusy} onClick={() => setCloseConfirmOpen(false)}>
              {t('services.reqKeepOpen')}
            </Button>
            <Button variant="destructive" disabled={actionBusy} onClick={runClose}>
              {actionBusy && <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />}
              {t('services.reqCloseConfirmAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw confirm dialog */}
      <Dialog open={withdrawConfirmOpen} onOpenChange={(open) => { if (!open && !actionBusy) setWithdrawConfirmOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <X size={20} aria-hidden="true" />
              {t('services.reqWithdrawConfirmTitle')}
            </DialogTitle>
            <DialogDescription>{t('services.reqWithdrawConfirmBody')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={actionBusy} onClick={() => setWithdrawConfirmOpen(false)}>
              {t('services.reqKeepOpen')}
            </Button>
            <Button variant="destructive" disabled={actionBusy} onClick={runWithdraw}>
              {actionBusy && <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />}
              {t('services.reqWithdrawConfirmAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav profile={profile} />
    </div>
  );
};

export default ServiceRequestDetail;
